// src/utils/dateUtils.js
// Unified date range utilities for all dashboards
//
// Alle periode-berekeningen zijn verankerd op Europe/Amsterdam, onafhankelijk van
// de timezone van de kijker én DST-veilig: dag-arithmetiek loopt via kalenderdagen
// (amsterdamMidnight) in plaats van vaste 24-uur stappen, zodat de overgang
// CET<->CEST (23/25-uursdagen) geen uur-verschuiving in de ranges geeft.

/**
 * Midnight Amsterdam voor een gegeven (jaar, maand, dag), uitgedrukt in UTC.
 * Maand is 1-based. Overflow (dag 0, dag 32, maand 13) wordt genormaliseerd,
 * dus amsterdamMidnight(2026, 7, -6) werkt gewoon.
 */
const amsterdamMidnight = (year, month, day) => {
  // Normaliseer overflow via UTC-noon (noon voorkomt datum-kantelen door offsets)
  const ref = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  const y = ref.getUTCFullYear();
  const m = ref.getUTCMonth();
  const d = ref.getUTCDate();

  // Dynamische Amsterdam-offset voor deze specifieke datum (CET +1 / CEST +2)
  const amsterdamHour = parseInt(ref.toLocaleString('en-GB', {
    timeZone: 'Europe/Amsterdam',
    hour: 'numeric',
    hour12: false
  }), 10);
  const offsetHours = amsterdamHour - 12;

  return new Date(Date.UTC(y, m, d, -offsetHours, 0, 0, 0));
};

/** YYYY-MM-DD van een Date, gezien vanuit Amsterdam. */
const ymdInAmsterdam = (date) =>
  date.toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' });

/** [jaar, maand(1-based), dag] van een Date, gezien vanuit Amsterdam. */
const getAmsterdamYMD = (date) => ymdInAmsterdam(date).split('-').map(Number);

/**
 * Verschuif een datum N hele Amsterdam-kalenderdagen (DST-veilig).
 * Resultaat is opnieuw midnight Amsterdam (in UTC).
 */
export const addAmsterdamDays = (date, days) => {
  const [y, m, d] = getAmsterdamYMD(date);
  return amsterdamMidnight(y, m, d + days);
};

/**
 * Get Amsterdam timezone date for consistent date calculations
 * This ensures all date calculations are based on Amsterdam timezone regardless of user's location
 * Returns a Date object representing midnight Amsterdam time
 */
export const getAmsterdamDate = () => {
  const [year, month, day] = getAmsterdamYMD(new Date());
  return amsterdamMidnight(year, month, day);
};

/**
 * Get Amsterdam timezone datetime for consistent time calculations
 */
export const getAmsterdamDateTime = () => {
  // Get current time parts in Amsterdam timezone
  const now = new Date();
  const options = { timeZone: 'Europe/Amsterdam', hour12: false };
  const dateStr = now.toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' });
  const timeStr = now.toLocaleTimeString('en-GB', options);

  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute, second] = timeStr.split(':').map(Number);

  // Return a Date object representing this Amsterdam time
  // Note: This returns a Date where getHours() etc return Amsterdam time values
  return new Date(year, month - 1, day, hour, minute, second);
};

/** Dagen sinds maandag (0 = maandag ... 6 = zondag), Amsterdam-weekdag. */
const daysFromMondayAmsterdam = () => {
  const dayOfWeek = getAmsterdamDateTime().getDay();
  return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
};

/**
 * Get date range for a given period.
 * Ranges zijn [start, end) — end is exclusief (server filtert met .lt).
 * Supports both standard periods and custom date ranges.
 */
export const getDateRange = (period, customStart = null, customEnd = null) => {
  // Custom periode: parse als Amsterdam-kalenderdagen (dynamische DST-offset,
  // niet meer op maandnummer geschat — dat zat fout in begin maart / eind oktober)
  if (period === 'custom' && customStart && customEnd) {
    const [sy, sm, sd] = customStart.split('-').map(Number);
    const [ey, em, ed] = customEnd.split('-').map(Number);
    return {
      start: amsterdamMidnight(sy, sm, sd),
      end: amsterdamMidnight(ey, em, ed + 1) // t/m einddag, exclusieve grens
    };
  }

  const todayAmsterdam = getAmsterdamDate();

  switch (period) {
    case 'today':
      return {
        start: todayAmsterdam,
        end: addAmsterdamDays(todayAmsterdam, 1)
      };

    case 'yesterday':
      return {
        start: addAmsterdamDays(todayAmsterdam, -1),
        end: todayAmsterdam
      };

    case 'thisWeek': {
      const thisWeekStart = addAmsterdamDays(todayAmsterdam, -daysFromMondayAmsterdam());
      return { start: thisWeekStart, end: addAmsterdamDays(thisWeekStart, 7) };
    }

    case 'lastWeek': {
      const lastWeekEnd = addAmsterdamDays(todayAmsterdam, -daysFromMondayAmsterdam());
      return { start: addAmsterdamDays(lastWeekEnd, -7), end: lastWeekEnd };
    }

    case 'thisMonth': {
      const [y, m] = getAmsterdamYMD(todayAmsterdam);
      return { start: amsterdamMidnight(y, m, 1), end: amsterdamMidnight(y, m + 1, 1) };
    }

    case 'lastMonth': {
      const [y, m] = getAmsterdamYMD(todayAmsterdam);
      return { start: amsterdamMidnight(y, m - 1, 1), end: amsterdamMidnight(y, m, 1) };
    }

    case 'last7days':
      // 7 kalenderdagen inclusief vandaag (was: 8 dagen door -7 + morgen)
      return {
        start: addAmsterdamDays(todayAmsterdam, -6),
        end: addAmsterdamDays(todayAmsterdam, 1)
      };

    case 'last30days':
      // 30 kalenderdagen inclusief vandaag (was: 31 dagen)
      return {
        start: addAmsterdamDays(todayAmsterdam, -29),
        end: addAmsterdamDays(todayAmsterdam, 1)
      };

    default:
      return {
        start: addAmsterdamDays(todayAmsterdam, -29),
        end: addAmsterdamDays(todayAmsterdam, 1)
      };
  }
};

/**
 * Get previous period range for comparison
 * Handles all period types with proper date calculations
 */
export const getPreviousPeriodRange = (period, customStart = null, customEnd = null) => {
  if (period === 'custom' && customStart && customEnd) {
    const { start, end } = getDateRange('custom', customStart, customEnd);
    const diffMs = end.getTime() - start.getTime();
    return {
      start: new Date(start.getTime() - diffMs),
      end: start
    };
  }

  const todayAmsterdam = getAmsterdamDate();

  switch (period) {
    case 'today':
      return {
        start: addAmsterdamDays(todayAmsterdam, -1),
        end: todayAmsterdam
      };

    case 'yesterday':
      return {
        start: addAmsterdamDays(todayAmsterdam, -2),
        end: addAmsterdamDays(todayAmsterdam, -1)
      };

    case 'thisWeek': {
      const daysFromMonday = daysFromMondayAmsterdam();
      const currentWeekStart = addAmsterdamDays(todayAmsterdam, -daysFromMonday);
      // Vorige week over hetzelfde aantal verstreken dagen (incl. vandaag-equivalent)
      const daysPassedInCurrentWeek = daysFromMonday + 1;
      const prevWeekStart = addAmsterdamDays(currentWeekStart, -7);
      return {
        start: prevWeekStart,
        end: addAmsterdamDays(prevWeekStart, daysPassedInCurrentWeek)
      };
    }

    case 'lastWeek': {
      const lastWeekEnd = addAmsterdamDays(todayAmsterdam, -daysFromMondayAmsterdam());
      const lastWeekStart = addAmsterdamDays(lastWeekEnd, -7);
      return { start: addAmsterdamDays(lastWeekStart, -7), end: lastWeekStart };
    }

    case 'thisMonth': {
      // Vorige maand over hetzelfde aantal verstreken dagen
      const [y, m, currentDayOfMonth] = getAmsterdamYMD(todayAmsterdam);
      return {
        start: amsterdamMidnight(y, m - 1, 1),
        end: amsterdamMidnight(y, m - 1, currentDayOfMonth)
      };
    }

    case 'lastMonth': {
      const [y, m] = getAmsterdamYMD(todayAmsterdam);
      return { start: amsterdamMidnight(y, m - 2, 1), end: amsterdamMidnight(y, m - 1, 1) };
    }

    case 'last7days':
      // De 7 dagen vóór het huidige 7-daagse venster
      return {
        start: addAmsterdamDays(todayAmsterdam, -13),
        end: addAmsterdamDays(todayAmsterdam, -6)
      };

    case 'last30days':
      return {
        start: addAmsterdamDays(todayAmsterdam, -59),
        end: addAmsterdamDays(todayAmsterdam, -29)
      };

    default:
      return {
        start: addAmsterdamDays(todayAmsterdam, -59),
        end: addAmsterdamDays(todayAmsterdam, -29)
      };
  }
};

/**
 * Get human-readable period labels
 */
export const getPeriodLabel = (period) => {
  const labels = {
    today: 'Vandaag',
    yesterday: 'Gisteren',
    thisWeek: 'Deze Week',
    lastWeek: 'Vorige Week',
    thisMonth: 'Deze Maand',
    lastMonth: 'Vorige Maand',
    last7days: 'Laatste 7 Dagen',
    last30days: 'Laatste 30 Dagen',
    custom: 'Aangepaste Periode'
  };
  return labels[period] || period;
};

/**
 * Validate if a period is supported
 */
export const isValidPeriod = (period) => {
  const validPeriods = [
    'today', 'yesterday', 'thisWeek', 'lastWeek',
    'thisMonth', 'lastMonth', 'last7days', 'last30days', 'custom'
  ];
  return validPeriods.includes(period);
};

/**
 * Get default period for new users/sessions
 */
export const getDefaultPeriod = () => 'today';

/**
 * Convert Amsterdam time to UTC for database queries
 * Database stores timestamps in UTC, so we need to convert Amsterdam time to UTC
 */
export const convertAmsterdamToUTC = (amsterdamDate) => {
  // Get the timezone offset for Amsterdam at the given date
  const amsTime = new Date(amsterdamDate.toLocaleString("en-US", {timeZone: "Europe/Amsterdam"}));
  const utcTime = new Date(amsterdamDate.toLocaleString("en-US", {timeZone: "UTC"}));

  // Calculate the offset difference in milliseconds
  const offsetMs = amsTime.getTime() - utcTime.getTime();

  // Apply the offset to convert Amsterdam time to UTC
  return new Date(amsterdamDate.getTime() - offsetMs);
};

/**
 * Format date range for display
 */
export const formatDateRange = (start, end) => {
  const formatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Amsterdam'
  };

  const startFormatted = start.toLocaleDateString('nl-NL', formatOptions);
  const endFormatted = end.toLocaleDateString('nl-NL', formatOptions);

  return `${startFormatted} - ${endFormatted}`;
};

/**
 * Get the appropriate granularity for chart trends based on selected period
 * @param {string} period - The selected period key
 * @returns {'day' | 'week' | 'month'} - The granularity level
 */
export const getGranularityForPeriod = (period) => {
  switch (period) {
    case 'today':
    case 'yesterday':
      return 'day';
    case 'thisWeek':
    case 'lastWeek':
    case 'last7days':
      return 'week';
    case 'thisMonth':
    case 'lastMonth':
    case 'last30days':
    case 'custom':
    default:
      return 'month';
  }
};

/**
 * Get extended date range for trend charts based on granularity.
 * Ranges zijn uitgelijnd op bucket-grenzen zodat de eerste bucket een VOLLE
 * week/maand is (geen misleidend laag eerste datapunt meer):
 * - Day granularity: 14 dagen t/m vandaag
 * - Week granularity: 12 weken — 11 volle weken + de lopende week
 * - Month granularity: 6 maanden — 5 volle maanden + de lopende maand
 * @param {string} period - The selected period key
 * @returns {Object} - { start, end } dates for trend data
 */
export const getTrendDateRange = (period) => {
  const todayAmsterdam = getAmsterdamDate();
  const granularity = getGranularityForPeriod(period);
  const end = addAmsterdamDays(todayAmsterdam, 1); // t/m vandaag

  if (granularity === 'week') {
    const currentWeekStart = addAmsterdamDays(todayAmsterdam, -daysFromMondayAmsterdam());
    return { start: addAmsterdamDays(currentWeekStart, -7 * 11), end };
  }

  if (granularity === 'month') {
    const [y, m] = getAmsterdamYMD(todayAmsterdam);
    return { start: amsterdamMidnight(y, m - 5, 1), end };
  }

  // day
  return { start: addAmsterdamDays(todayAmsterdam, -13), end };
};

/**
 * Get previous trend date range for comparison.
 * Zelfde aantal buckets, direct vóór de huidige trend-range (bucket-gealigneerd).
 * @param {string} period - The selected period key
 * @returns {Object} - { start, end } dates for previous trend data
 */
export const getPreviousTrendDateRange = (period) => {
  const granularity = getGranularityForPeriod(period);
  const { start: currentStart } = getTrendDateRange(period);

  if (granularity === 'week') {
    return { start: addAmsterdamDays(currentStart, -7 * 12), end: currentStart };
  }

  if (granularity === 'month') {
    const [y, m] = getAmsterdamYMD(currentStart);
    return { start: amsterdamMidnight(y, m - 6, 1), end: currentStart };
  }

  return { start: addAmsterdamDays(currentStart, -14), end: currentStart };
};

/**
 * Enumereer alle bucket-keys (YYYY-MM-DD) binnen een trend-range.
 * Wordt gebruikt om dagen/weken/maanden zónder calls als 0-punt in de grafiek
 * te zetten (anders vallen ze weg en verschuiven de vergelijkingslijnen).
 * @param {{start: Date, end: Date}} range - end is exclusief
 * @param {'day' | 'week' | 'month'} granularity
 * @returns {string[]} - periodKeys, oplopend gesorteerd
 */
export const enumerateTrendPeriodKeys = (range, granularity) => {
  if (!range || !range.start || !range.end) return [];
  const endStr = ymdInAmsterdam(range.end); // exclusieve grens
  const keys = [];

  let cursor = granularity === 'week'
    ? getWeekStart(range.start)
    : granularity === 'month'
      ? getMonthStart(range.start)
      : ymdInAmsterdam(range.start);

  while (cursor < endStr && keys.length < 400) {
    keys.push(cursor);
    const [y, m, d] = cursor.split('-').map(Number);
    const next = granularity === 'month'
      ? new Date(Date.UTC(y, m, 1, 12)) // m is 1-based: index m = volgende maand
      : new Date(Date.UTC(y, m - 1, d + (granularity === 'week' ? 7 : 1), 12));
    cursor = next.toISOString().split('T')[0];
  }

  return keys;
};

/**
 * Get the start of a week (Monday) for a given date in Amsterdam timezone
 * @param {Date} date - The date to get the week start for
 * @returns {string} - ISO date string (YYYY-MM-DD) for Monday of that week
 *
 * NB: rekent in UTC-noon zodat toISOString() geen dag terugvalt voor kijkers
 * in UTC+ timezones (de oude lokale-middernacht variant gaf zondag-keys en
 * daardoor weeklabels die één week te laag waren).
 */
export const getWeekStart = (date) => {
  const [year, month, day] = getAmsterdamYMD(date);
  const d = new Date(Date.UTC(year, month - 1, day, 12));
  const dayOfWeek = d.getUTCDay();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  d.setUTCDate(d.getUTCDate() - daysFromMonday);
  return d.toISOString().split('T')[0];
};

/**
 * Get the start of a month for a given date in Amsterdam timezone
 * @param {Date} date - The date to get the month start for
 * @returns {string} - ISO date string (YYYY-MM-DD) for first of that month
 */
export const getMonthStart = (date) => {
  const dateStr = date.toLocaleDateString('en-CA', { timeZone: 'Europe/Amsterdam' });
  const [year, month] = dateStr.split('-').map(Number);
  return `${year}-${String(month).padStart(2, '0')}-01`;
};

/**
 * Format a period key for display in charts
 * @param {string} periodKey - The period key (date, week start, or month start)
 * @param {'day' | 'week' | 'month'} granularity - The granularity level
 * @returns {string} - Formatted label for the chart
 */
export const formatTrendLabel = (periodKey, granularity) => {
  const date = new Date(periodKey + 'T12:00:00');

  switch (granularity) {
    case 'day':
      // "4 feb" format
      return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    case 'week':
      // "W5" format (week number)
      const weekNumber = getWeekNumber(date);
      return `W${weekNumber}`;
    case 'month':
      // "Feb" format
      return date.toLocaleDateString('nl-NL', { month: 'short' });
    default:
      return periodKey;
  }
};

/**
 * Get ISO week number for a date
 * @param {Date} date - The date
 * @returns {number} - Week number (1-53)
 */
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};
